import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ImpulseSpinner } from 'react-spinners-kit';
import PropTypes from 'prop-types';
import cn from 'classnames';

import { ReactComponent as AiSparkleIcon } from 'assets/ai-suggested-sparkle.svg';
import { ReactComponent as Asterisk } from 'assets/asterisk.svg';
import chevronDownSmall from 'assets/chevron-down-small.svg';
import closeIcon from 'assets/close-modal.svg';

import { infoReactToastify } from 'utlis/toasts';

import AnalyticsUpload from './AnalyticsUpload';
import CharCounter from './CharCounter';
import {
  BUSINESS_GOAL_OPTIONS_BY_CATEGORY,
  CHIP_OPTIONS_BY_CATEGORY,
  FIELD_MAX_LENGTHS,
  MAX_INPUT_LENGTH,
  MIN_PERSONA_INPUT_LENGTH,
  REVIEW_CATEGORIES,
  sanitizeInputText,
} from './constants';
import PersonaDropdown from './PersonaDropdown';
import SlashMenu from './SlashMenu';
import useSlashMenuField from './useSlashMenuField';

import styles from './styles.module.scss';

const ProductForm = ({
  productDescription,
  setProductDescription,
  title,
  setTitle,
  audience,
  setAudience,
  businessGoal,
  setBusinessGoal,
  mode = 'form',
  reviewData,
  selectedCategory,
  selectedProblemChips,
  setSelectedProblemChips,
  // AI inference props
  aiFieldStates,
  isLoadingAiSuggestions,
  hasAiSuggestions,
  onRestoreAiSuggestions,
  onFieldEdit,
  onRestoreFieldAi,
  // Analytics props
  analyticsUploadState,
  analyticsFiles,
  onAnalyticsUpload,
  onAnalyticsRemove,
  disabled,
  observedAnalytics,
  setObservedAnalytics,
  // Persona props
  personas,
  onCreatePersona,
  onSelectPersona,
  onDeselectPersona,
  selectedPersona,
  isGeneratingPersona,
  isViewer,
}) => {
  const isDisplayMode = useMemo(() => mode === 'display', [mode]);
  const hasExistingReview = useMemo(() => (
    reviewData
    && typeof reviewData === 'object'
    && Object.keys(reviewData).length > 0
  ), [reviewData]);
  const shouldShowFullForm = !hasExistingReview;

  const [personaDropdownOpen, setPersonaDropdownOpen] = useState(false);
  const [personaHighlightedIndex, setPersonaHighlightedIndex] = useState(-1);
  const hasPersonas = Array.isArray(personas) && personas.length > 0;

  const personaPreviewText = personaDropdownOpen && personaHighlightedIndex >= 0 && personas[personaHighlightedIndex]
    ? personas[personaHighlightedIndex].standard_summary
    : null;

  const personaWrapperRef = useRef(null);

  const handlePersonaDropdownClose = useCallback(() => {
    setPersonaDropdownOpen(false);
    setPersonaHighlightedIndex(-1);
  }, []);

  // Close persona dropdown when clicking outside
  useEffect(() => {
    if (!personaDropdownOpen) {
      return;
    }

    function handleClickOutside(e) {
      if (personaWrapperRef.current && !personaWrapperRef.current.contains(e.target)) {
        handlePersonaDropdownClose();
      }
    }

    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [personaDropdownOpen, handlePersonaDropdownClose]);

  const handlePersonaSelect = useCallback(persona => {
    onSelectPersona(persona);
    handlePersonaDropdownClose();
  }, [onSelectPersona, handlePersonaDropdownClose]);

  const handlePersonaKeyDown = useCallback(e => {
    if (!personaDropdownOpen) {
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setPersonaHighlightedIndex(prev => Math.min(prev + 1, personas.length - 1));
    }
    else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setPersonaHighlightedIndex(prev => Math.max(prev - 1, -1));
    }
    else if (e.key === 'Enter') {
      e.preventDefault();

      if (personaHighlightedIndex >= 0) {
        handlePersonaSelect(personas[personaHighlightedIndex]);
      }
    }
    else if (e.key === 'Escape') {
      handlePersonaDropdownClose();
    }
  }, [personaDropdownOpen, personaHighlightedIndex, personas, handlePersonaSelect, handlePersonaDropdownClose]);

  // Derived AI states per field — only active in form mode
  const isTitleAi = !isDisplayMode && aiFieldStates?.title === 'ai';
  const isAudienceAi = !isDisplayMode && aiFieldStates?.audience === 'ai';
  const isProblemAi = !isDisplayMode && aiFieldStates?.problem === 'ai';
  const isBusinessGoalAi = !isDisplayMode && aiFieldStates?.businessGoal === 'ai';
  const isObservedAnalyticsAi = !isDisplayMode && aiFieldStates?.observedAnalytics === 'ai';

  // Analytics processing (upload + enrichment) drives the loading state for all
  // context fields, including the observed analytics field which appears as soon
  // as an upload starts.
  const isAnalyticsProcessing = analyticsUploadState === 'uploading' || analyticsUploadState === 'in-progress';
  const showSkeleton = !isDisplayMode && (Boolean(isLoadingAiSuggestions) || isAnalyticsProcessing);
  const showObservedAnalytics = !isDisplayMode && (Boolean(observedAnalytics) || isAnalyticsProcessing);

  // conditions for persona dropdown
  const canCreatePersona = audience.trim().length >= MIN_PERSONA_INPUT_LENGTH;
  const hasAudienceContent = audience.trim().length > 0;
  const showCreatePersonaButton = !isDisplayMode && !isViewer && hasAudienceContent
    && !hasPersonas && !showSkeleton && !isGeneratingPersona;
  const showPersonaDropdownTrigger = !isDisplayMode && !isViewer && hasPersonas
    && !showSkeleton && !isGeneratingPersona;
  const showGeneratingState = !isDisplayMode && !isViewer && isGeneratingPersona && !showSkeleton;

  // Pre-computed booleans to keep className lines within length limits
  const titleNormal = !isDisplayMode && !isTitleAi && !showSkeleton;
  const audienceNormal = !isDisplayMode && !isAudienceAi && !showSkeleton;
  const problemNormal = !isDisplayMode && !isProblemAi && !showSkeleton;
  const businessGoalNormal = !isDisplayMode && !isBusinessGoalAi && !showSkeleton;

  const displayTitle = useMemo(
    () => reviewData?.title || title || 'No title provided',
    [reviewData?.title, title]
  );

  const displayProduct = useMemo(
    () => reviewData?.extra_data?.user_problem || productDescription || 'No product description provided',
    [reviewData?.extra_data, productDescription]
  );

  const displayAudience = useMemo(
    () => reviewData?.extra_data?.audience || audience || 'No audience provided',
    [reviewData?.extra_data, audience]
  );

  const displayBusinessGoal = useMemo(
    () => reviewData?.extra_data?.business_goal || businessGoal || 'No business goal provided',
    [reviewData?.extra_data, businessGoal]
  );

  const displayObservedAnalytics = useMemo(
    () => reviewData?.extra_data?.observed_analytics,
    [reviewData?.extra_data]
  );

  const displayCategory = useMemo(() => {
    const auditCategory = reviewData?.extra_data?.template || reviewData?.extra_data?.template;

    return auditCategory || selectedCategory;
  }, [reviewData?.extra_data, selectedCategory]);

  const activeCategory = displayCategory || REVIEW_CATEGORIES[0].key;

  const chipOptions = useMemo(
    () => CHIP_OPTIONS_BY_CATEGORY[activeCategory] || CHIP_OPTIONS_BY_CATEGORY.ux_critique,
    [activeCategory]
  );

  const businessGoalChipOptions = useMemo(
    () => BUSINESS_GOAL_OPTIONS_BY_CATEGORY[activeCategory] || BUSINESS_GOAL_OPTIONS_BY_CATEGORY.ux_critique,
    [activeCategory]
  );

  const {
    dropdownOpen: problemDropdownOpen,
    highlightedIndex: problemHighlightedIndex,
    setHighlightedIndex: setProblemHighlightedIndex,
    previewText: problemPreviewText,
    handleSelect: handleSelectProblemTemplate,
    closeDropdown: closeProblemDropdown,
    handleChange: handleProblemChange,
    handleKeyDown: handleProblemKeyDown,
  } = useSlashMenuField({
    options: chipOptions,
    value: productDescription,
    setValue: setProductDescription,
    onFieldEdit,
    fieldName: 'problem',
    isDisplayMode,
    maxLength: FIELD_MAX_LENGTHS.problem,
    onAfterSelect: option => setSelectedProblemChips([option.label]),
    onAfterChange: () => {
      if (Array.isArray(selectedProblemChips) && selectedProblemChips.length > 0) {
        setSelectedProblemChips([]);
      }
    },
  });

  const {
    dropdownOpen: businessGoalDropdownOpen,
    highlightedIndex: businessGoalHighlightedIndex,
    setHighlightedIndex: setBusinessGoalHighlightedIndex,
    previewText: businessGoalPreviewText,
    handleSelect: handleSelectBusinessGoalTemplate,
    closeDropdown: closeBusinessGoalDropdown,
    handleChange: handleBusinessGoalChange,
    handleKeyDown: handleBusinessGoalKeyDown,
  } = useSlashMenuField({
    options: businessGoalChipOptions,
    value: businessGoal,
    setValue: setBusinessGoal,
    onFieldEdit,
    fieldName: 'businessGoal',
    isDisplayMode,
  });

  const handleAudienceChange = event => {
    if (isDisplayMode) {
      return;
    }

    const sanitizedValue = sanitizeInputText(event.target.value, FIELD_MAX_LENGTHS.audience);

    onFieldEdit?.('audience');
    setAudience(sanitizedValue);
  };

  useEffect(() => {
    if (isDisplayMode) {
      return;
    }

    if (typeof productDescription === 'string') {
      const sanitized = sanitizeInputText(productDescription, FIELD_MAX_LENGTHS.problem);

      if (sanitized !== productDescription) {
        setProductDescription(sanitized);
      }
    }

    if (typeof audience === 'string') {
      const sanitized = sanitizeInputText(audience, FIELD_MAX_LENGTHS.audience);

      if (sanitized !== audience) {
        setAudience(sanitized);
      }
    }

    if (typeof businessGoal === 'string') {
      const sanitized = sanitizeInputText(businessGoal, FIELD_MAX_LENGTHS.businessGoal);

      if (sanitized !== businessGoal) {
        setBusinessGoal(sanitized);
      }
    }
  }, [isDisplayMode, productDescription, setProductDescription, audience, setAudience, businessGoal, setBusinessGoal]);

  // Computed display values
  const displayedAudienceValue = isDisplayMode
    ? (displayAudience || '')
    : (personaPreviewText !== null ? personaPreviewText : sanitizeInputText(audience, FIELD_MAX_LENGTHS.audience));

  const displayedBusinessGoalValue = isDisplayMode
    ? (displayBusinessGoal || '')
    : sanitizeInputText(businessGoal, FIELD_MAX_LENGTHS.businessGoal);

  const problemTextareaValue = isDisplayMode
    ? (displayProduct || '')
    : (problemPreviewText !== null
      ? problemPreviewText
      : sanitizeInputText(productDescription, FIELD_MAX_LENGTHS.problem));

  const businessGoalTextareaValue = isDisplayMode
    ? displayedBusinessGoalValue
    : (businessGoalPreviewText !== null
      ? businessGoalPreviewText
      : sanitizeInputText(businessGoal, FIELD_MAX_LENGTHS.businessGoal));

  // Refs for auto-growing textareas in display mode
  const audienceTextareaRef = useRef(null);
  const problemTextareaRef = useRef(null);
  const businessGoalTextareaRef = useRef(null);
  const observedAnalyticsTextareaRef = useRef(null);

  // Expand textarea to fit all content for display mode
  const expandTextareaToContent = textarea => {
    if (!textarea || !isDisplayMode) {
      return;
    }

    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  };

  // Expand textareas in display mode when content loads
  useEffect(() => {
    if (isDisplayMode) {
      // Small delay to ensure DOM is updated with content
      const timer = setTimeout(() => {
        if (audienceTextareaRef.current) {
          expandTextareaToContent(audienceTextareaRef.current);
        }
        if (problemTextareaRef.current) {
          expandTextareaToContent(problemTextareaRef.current);
        }
        if (businessGoalTextareaRef.current) {
          expandTextareaToContent(businessGoalTextareaRef.current);
        }
        if (observedAnalyticsTextareaRef.current) {
          expandTextareaToContent(observedAnalyticsTextareaRef.current);
        }
      }, 0);

      return () => clearTimeout(timer);
    }
  }, [
    isDisplayMode,
    displayedAudienceValue,
    problemTextareaValue,
    displayedBusinessGoalValue,
    displayObservedAnalytics,
  ]);

  return (
    <div
      className={cn('flex flex-col gap-10 2xl:gap-12 overflow-auto pb-0', {
        'p-0': isDisplayMode,
        'p-7': !isDisplayMode,
      })}
    >
      {shouldShowFullForm && !isDisplayMode && (
        <div className={cn(styles['context-heading'], 'items-center')}>
          <h2 className={styles['context-heading__title']}>
            Add your context
          </h2>
          <div className="flex flex-col items-end gap-8 ml-auto">
            {/* Clickable AI-inferred legend — restores all AI suggestions */}
            <button
              type="button"
              onClick={hasAiSuggestions ? onRestoreAiSuggestions : undefined}
              className={cn(styles['ai-legend-text'], {
                [styles['ai-legend-text--clickable']]: hasAiSuggestions,
              })}
            >
              AI-inferred
              <AiSparkleIcon className={cn('w-3 h-3 flex-shrink-0', styles['sparkle--ai'])} />
            </button>
            <span className={styles['ai-legend-text']}>
              Required
              <Asterisk />
            </span>
          </div>
        </div>
      )}

      <div
        className={cn(
          'flex flex-col gap-10 2xl:gap-12 pr-8 pl-1',
          {
            'overflow-y-auto': isDisplayMode,
          }
        )}
      >
        {shouldShowFullForm && (
          <>
            <label className="text-base text-gray-900 h-5">
              Title
              {' '}
              <span className="text-gray-400">
                *
              </span>
            </label>
            {/* Title field with sparkle icon */}
            <div className="relative 2xl:mb-3 mb-5">
              {!isDisplayMode && (
                <span
                  className={cn(styles['sparkle-wrapper'], styles['sparkle-wrapper--centered'], {
                    [styles['sparkle-wrapper--clickable']]: hasAiSuggestions && !showSkeleton,
                  })}
                  onClick={hasAiSuggestions ? () => onRestoreFieldAi('title') : undefined}
                >
                  <AiSparkleIcon
                    className={cn('w-4 h-4 flex-shrink-0', {
                      [styles['sparkle--ai']]: isTitleAi && !showSkeleton,
                      [styles['sparkle--default']]: !isTitleAi || showSkeleton,
                    })}
                  />
                </span>
              )}
              {showSkeleton && (
                <div className={styles['skeleton-overlay']}>
                  <div className={cn(styles['skeleton-bar'])} />
                </div>
              )}
              <input
                value={isDisplayMode ? displayTitle : title}
                onChange={e => {
                  onFieldEdit?.('title');
                  setTitle(e.target.value);
                }}
                placeholder={showSkeleton ? '' : 'Name of review'}
                readOnly={isDisplayMode || showSkeleton}
                className={cn(
                  'w-full rounded-lg text-sm resize-none focus:outline-none p-3 pr-10',
                  {
                    'bg-gray-50 text-gray-500 border-0': isDisplayMode,
                    'border text-gray-600 placeholder-gray-500': !isDisplayMode,
                    'border-gray-300': titleNormal || (!isDisplayMode && showSkeleton),
                    'focus:ring-2 focus:ring-primary-400 focus:border-transparent': titleNormal,
                    [styles['field-border--ai']]: !isDisplayMode && isTitleAi && !showSkeleton,
                  }
                )}
              />
            </div>
          </>
        )}

        {(shouldShowFullForm || displayedAudienceValue) && (
          <>
            <div className="flex items-center justify-between">
              <label className="text-base font-medium text-gray-900 h-5">
                Audience
              </label>
              {showCreatePersonaButton && (
                <button
                  type="button"
                  onClick={() => {
                    if (canCreatePersona) {
                      onCreatePersona();
                    }
                    else {
                      infoReactToastify(
                        'Add more audience context to create a persona (minimum 140 characters)',
                        { autoClose: 3000, position: 'bottom-center', hideProgressBar: true }
                      );
                    }
                  }}
                  className={cn(
                    styles['persona-trigger'],
                    { [styles['persona-trigger--disabled']]: !canCreatePersona }
                  )}
                >
                  Create persona
                </button>
              )}
              {showGeneratingState && (
                <span className={cn(styles['persona-trigger'], styles['persona-trigger--disabled'])}>
                  Generating
                  <ImpulseSpinner size={16} frontColor="#121723" />
                </span>
              )}
              {showPersonaDropdownTrigger && (
                <div ref={personaWrapperRef} className="relative" onKeyDown={handlePersonaKeyDown}>
                  <button
                    type="button"
                    onClick={() => {
                      if (personaDropdownOpen) {
                        handlePersonaDropdownClose();
                      }
                      else {
                        setPersonaDropdownOpen(true);
                        setPersonaHighlightedIndex(-1);
                      }
                    }}
                    className={cn(
                      styles['persona-trigger'],
                      { [styles['persona-trigger--open']]: personaDropdownOpen }
                    )}
                  >
                    User personas
                    <img
                      src={chevronDownSmall}
                      alt="chevron icon"
                      className={cn(styles['persona-trigger-icon'], {
                        [styles['persona-trigger-icon--open']]: personaDropdownOpen,
                      })}
                    />
                  </button>
                  <PersonaDropdown
                    isOpen={personaDropdownOpen}
                    personas={personas}
                    highlightedIndex={personaHighlightedIndex}
                    onHover={setPersonaHighlightedIndex}
                    onSelect={handlePersonaSelect}
                    canCreatePersona={canCreatePersona}
                    onCreatePersona={() => {
                      handlePersonaDropdownClose();
                      onCreatePersona();
                    }}
                  />
                </div>
              )}
            </div>
            {/* Audience field with sparkle icon */}
            <div className="relative">
              {!isDisplayMode && (
                <span
                  className={cn(styles['sparkle-wrapper'], {
                    [styles['sparkle-wrapper--clickable']]: hasAiSuggestions && !showSkeleton,
                  })}
                  onClick={hasAiSuggestions ? () => onRestoreFieldAi('audience') : undefined}
                >
                  <AiSparkleIcon
                    className={cn('w-4 h-4 flex-shrink-0', {
                      [styles['sparkle--ai']]: isAudienceAi && !showSkeleton,
                      [styles['sparkle--default']]: !isAudienceAi || showSkeleton,
                    })}
                  />
                </span>
              )}
              {showSkeleton && (
                <div className={styles['skeleton-overlay']}>
                  <div className={styles['skeleton-bar']} />
                  <div className={styles['skeleton-bar']} />
                  <div className={styles['skeleton-bar']} />
                  <div className={cn(styles['skeleton-bar'], styles['skeleton-bar--55'])} />
                </div>
              )}
              {!isDisplayMode && selectedPersona && !showSkeleton && personaPreviewText === null
                ? (
                  <div
                    className={cn(
                      styles['audience-persona-field'],
                      styles['textarea--no-scrollbar'],
                      {
                        'border-gray-300': audienceNormal,
                        [styles['field-border--ai']]: isAudienceAi,
                      }
                    )}
                  >
                    <div className={styles['persona-badge-wrapper']}>
                      <span className={styles['persona-badge']}>
                        <span className={styles['persona-badge__name']}>
                          {selectedPersona.name}
                        </span>
                        <button
                          type="button"
                          className={styles['persona-badge__remove']}
                          onClick={onDeselectPersona}
                        >
                          <img src={closeIcon} alt="Remove" className={styles['persona-badge__remove-icon']} />
                        </button>
                      </span>
                    </div>
                    <textarea
                      ref={audienceTextareaRef}
                      value={displayedAudienceValue}
                      onChange={handleAudienceChange}
                      placeholder="Who needs this feature"
                      maxLength={MAX_INPUT_LENGTH}
                      className={cn(
                        styles['audience-persona-textarea'],
                        styles['textarea--no-scrollbar']
                      )}
                    />
                  </div>
                )
                : (
                  <textarea
                    ref={audienceTextareaRef}
                    value={showSkeleton ? '' : displayedAudienceValue}
                    onChange={handleAudienceChange}
                    placeholder={showSkeleton ? '' : 'Who needs this feature'}
                    readOnly={isDisplayMode || showSkeleton || personaPreviewText !== null}
                    maxLength={FIELD_MAX_LENGTHS.audience}
                    className={cn(
                      'w-full rounded-lg text-sm resize-none focus:outline-none p-3 pr-10',
                      styles['textarea--no-scrollbar'],
                      {
                        'bg-gray-50 text-gray-500 border-0 mb-5 min-h-auto': isDisplayMode,
                        'border text-gray-600 placeholder-gray-500 min-h-24 overflow-y-auto': !isDisplayMode,
                        'border-gray-300': audienceNormal || (!isDisplayMode && showSkeleton),
                        'focus:ring-2 focus:ring-primary-400 focus:border-transparent': audienceNormal,
                        'max-h-28': !isDisplayMode,
                        [styles['field-border--ai']]: !isDisplayMode && isAudienceAi && !showSkeleton,
                        [styles['textarea--preview']]: personaPreviewText !== null,
                      }
                    )}
                  />
                )}
            </div>
            {!isDisplayMode && (
              <CharCounter
                value={displayedAudienceValue}
                maxLength={FIELD_MAX_LENGTHS.audience}
              />
            )}
          </>
        )}

        <label className="text-base text-gray-900 h-5">
          Problem to solve
        </label>
        {/* Problem field with sparkle icon and slash menu */}
        <div className="relative">
          {!isDisplayMode && problemDropdownOpen && (
            <SlashMenu
              options={chipOptions}
              highlightedIndex={problemHighlightedIndex}
              onHover={setProblemHighlightedIndex}
              onSelect={handleSelectProblemTemplate}
            />
          )}
          {!isDisplayMode && (
            <span
              className={cn(styles['sparkle-wrapper'], {
                [styles['sparkle-wrapper--clickable']]: hasAiSuggestions && !showSkeleton,
              })}
              onClick={hasAiSuggestions ? () => onRestoreFieldAi('problem') : undefined}
            >
              <AiSparkleIcon
                className={cn('w-4 h-4 flex-shrink-0', {
                  [styles['sparkle--ai']]: isProblemAi && !showSkeleton,
                  [styles['sparkle--default']]: !isProblemAi || showSkeleton,
                })}
              />
            </span>
          )}
          {showSkeleton && (
            <div className={styles['skeleton-overlay']}>
              <div className={styles['skeleton-bar']} />
              <div className={styles['skeleton-bar']} />
              <div className={styles['skeleton-bar']} />
              <div className={cn(styles['skeleton-bar'], styles['skeleton-bar--55'])} />
            </div>
          )}
          <textarea
            ref={problemTextareaRef}
            value={showSkeleton ? '' : problemTextareaValue}
            onChange={handleProblemChange}
            onKeyDown={handleProblemKeyDown}
            onBlur={closeProblemDropdown}
            placeholder={showSkeleton ? '' : 'Type / for templates'}
            readOnly={isDisplayMode || problemPreviewText !== null || showSkeleton}
            maxLength={FIELD_MAX_LENGTHS.problem}
            className={cn(
              'w-full rounded-lg text-sm resize-none focus:outline-none p-3 pr-10',
              styles['textarea--no-scrollbar'],
              {
                'bg-gray-50 text-gray-500 border-0 mb-5 min-h-auto overflow-hidden': isDisplayMode,
                'border text-gray-600 placeholder-gray-500 min-h-24 overflow-y-auto': !isDisplayMode,
                'border-gray-300': problemNormal || (!isDisplayMode && showSkeleton),
                'focus:ring-2 focus:ring-primary-400 focus:border-transparent': problemNormal,
                'max-h-28 h-1/2 2xl:h-16': !isDisplayMode,
                [styles['textarea--preview']]: problemPreviewText !== null,
                [styles['field-border--ai']]: !isDisplayMode && isProblemAi && !showSkeleton,
              }
            )}
          />
        </div>

        {!isDisplayMode && (
          <CharCounter
            value={productDescription}
            maxLength={FIELD_MAX_LENGTHS.problem}
          />
        )}

        {!isDisplayMode && (
          <>
            <label className="text-base text-gray-900 h-5">
              Analytics
            </label>
            <AnalyticsUpload
              analyticsUploadState={analyticsUploadState}
              analyticsFiles={analyticsFiles}
              onAnalyticsUpload={onAnalyticsUpload}
              onAnalyticsRemove={onAnalyticsRemove}
              disabled={disabled}
              compact={showObservedAnalytics}
            />
          </>
        )}

        {/*
          Observed Analytics field — no label, sits directly under the analytics
          box. Appears as soon as an upload starts and shows a loading skeleton
          while the enrichment request is in flight.
        */}
        {showObservedAnalytics && (
          <>
            <div className="relative">
              <span className={styles['sparkle-wrapper']}>
                <AiSparkleIcon
                  className={cn('w-4 h-4 flex-shrink-0', {
                    [styles['sparkle--ai']]: isObservedAnalyticsAi && !showSkeleton,
                    [styles['sparkle--default']]: !isObservedAnalyticsAi || showSkeleton,
                  })}
                />
              </span>
              {showSkeleton && (
                <div className={styles['skeleton-overlay']}>
                  <div className={styles['skeleton-bar']} />
                  <div className={styles['skeleton-bar']} />
                  <div className={styles['skeleton-bar']} />
                  <div className={cn(styles['skeleton-bar'], styles['skeleton-bar--55'])} />
                </div>
              )}
              <textarea
                value={showSkeleton ? '' : observedAnalytics}
                onChange={e => {
                  onFieldEdit?.('observedAnalytics');
                  setObservedAnalytics(e.target.value);
                }}
                placeholder={showSkeleton ? '' : 'AI-generated insights from analytics'}
                readOnly={isDisplayMode || showSkeleton}
                maxLength={FIELD_MAX_LENGTHS.observedAnalytics}
                className={cn(
                  'w-full rounded-lg text-sm resize-none focus:outline-none p-3 pr-10',
                  styles['textarea--no-scrollbar'],
                  {
                    'border text-gray-600 placeholder-gray-500 min-h-24 overflow-y-auto': !isDisplayMode,
                    'border-gray-300': !isObservedAnalyticsAi || showSkeleton,
                    'focus:ring-2 focus:ring-primary-400 focus:border-transparent':
                      !isObservedAnalyticsAi && !showSkeleton,
                    'max-h-28': !isDisplayMode,
                    [styles['field-border--ai']]: isObservedAnalyticsAi && !showSkeleton,
                  }
                )}
              />
            </div>
            {!showSkeleton && (
              <CharCounter
                value={observedAnalytics}
                maxLength={FIELD_MAX_LENGTHS.observedAnalytics}
              />
            )}
          </>
        )}

        {(shouldShowFullForm || displayedBusinessGoalValue) && (
          <>
            <label className="text-base text-gray-900 h-5">
              Business goal
            </label>
            {/* Business goal field with sparkle icon and slash menu */}
            <div className="relative">
              {!isDisplayMode && businessGoalDropdownOpen && (
                <SlashMenu
                  options={businessGoalChipOptions}
                  highlightedIndex={businessGoalHighlightedIndex}
                  onHover={setBusinessGoalHighlightedIndex}
                  onSelect={handleSelectBusinessGoalTemplate}
                />
              )}
              {!isDisplayMode && (
                <span
                  className={cn(styles['sparkle-wrapper'], {
                    [styles['sparkle-wrapper--clickable']]: hasAiSuggestions && !showSkeleton,
                  })}
                  onClick={hasAiSuggestions ? () => onRestoreFieldAi('businessGoal') : undefined}
                >
                  <AiSparkleIcon
                    className={cn('w-4 h-4 flex-shrink-0', {
                      [styles['sparkle--ai']]: isBusinessGoalAi && !showSkeleton,
                      [styles['sparkle--default']]: !isBusinessGoalAi || showSkeleton,
                    })}
                  />
                </span>
              )}
              {showSkeleton && (
                <div className={styles['skeleton-overlay']}>
                  <div className={styles['skeleton-bar']} />
                  <div className={styles['skeleton-bar']} />
                  <div className={styles['skeleton-bar']} />
                  <div className={cn(styles['skeleton-bar'], styles['skeleton-bar--55'])} />
                </div>
              )}
              <textarea
                ref={businessGoalTextareaRef}
                value={showSkeleton ? '' : businessGoalTextareaValue}
                onChange={handleBusinessGoalChange}
                onKeyDown={handleBusinessGoalKeyDown}
                onBlur={closeBusinessGoalDropdown}
                placeholder={showSkeleton ? '' : 'Type / for templates'}
                readOnly={isDisplayMode || businessGoalPreviewText !== null || showSkeleton}
                maxLength={FIELD_MAX_LENGTHS.businessGoal}
                className={cn(
                  'w-full rounded-lg text-sm resize-none focus:outline-none p-3 pr-10',
                  styles['textarea--no-scrollbar'],
                  {
                    'bg-gray-50 text-gray-500 border-0 mb-5 min-h-auto overflow-hidden': isDisplayMode,
                    'border text-gray-600 placeholder-gray-500': !isDisplayMode,
                    'min-h-24 overflow-y-auto max-h-28': !isDisplayMode,
                    'border-gray-300': businessGoalNormal || (!isDisplayMode && showSkeleton),
                    'focus:ring-2 focus:ring-primary-400 focus:border-transparent': businessGoalNormal,
                    [styles['textarea--preview']]: businessGoalPreviewText !== null,
                    [styles['field-border--ai']]: !isDisplayMode && isBusinessGoalAi && !showSkeleton,
                  }
                )}
              />
            </div>
            {!isDisplayMode && (
              <CharCounter
                value={businessGoal}
                maxLength={FIELD_MAX_LENGTHS.businessGoal}
              />
            )}
          </>
        )}

        {isDisplayMode && displayObservedAnalytics && (
          <>
            <label className="text-base text-gray-900 h-5">
              Observed Analytics
            </label>
            <textarea
              ref={observedAnalyticsTextareaRef}
              value={displayObservedAnalytics}
              readOnly
              className={cn(
                'w-full rounded-lg text-sm resize-none focus:outline-none p-3',
                styles['textarea--no-scrollbar'],
                'bg-gray-50 text-gray-500 border-0 mb-5 min-h-auto overflow-hidden'
              )}
            />
          </>
        )}
      </div>
    </div>
  );
};

ProductForm.propTypes = {
  productDescription: PropTypes.string,
  setProductDescription: PropTypes.func,
  title: PropTypes.string,
  setTitle: PropTypes.func,
  audience: PropTypes.string,
  setAudience: PropTypes.func,
  businessGoal: PropTypes.string,
  setBusinessGoal: PropTypes.func,
  mode: PropTypes.oneOf(['form', 'display']),
  reviewData: PropTypes.shape({
    title: PropTypes.string,
    extra_data: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  }),
  selectedCategory: PropTypes.string,
  selectedProblemChips: PropTypes.arrayOf(PropTypes.string),
  setSelectedProblemChips: PropTypes.func,
  aiFieldStates: PropTypes.shape({
    title: PropTypes.string,
    audience: PropTypes.string,
    problem: PropTypes.string,
    businessGoal: PropTypes.string,
    observedAnalytics: PropTypes.string,
  }),
  isLoadingAiSuggestions: PropTypes.bool,
  hasAiSuggestions: PropTypes.bool,
  onRestoreAiSuggestions: PropTypes.func,
  onFieldEdit: PropTypes.func,
  onRestoreFieldAi: PropTypes.func,
  analyticsUploadState: PropTypes.oneOf([null, 'uploading', 'in-progress', 'done']),
  analyticsFiles: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    previewUrl: PropTypes.string,
  })),
  onAnalyticsUpload: PropTypes.func,
  onAnalyticsRemove: PropTypes.func,
  disabled: PropTypes.bool,
  observedAnalytics: PropTypes.string,
  setObservedAnalytics: PropTypes.func,
  personas: PropTypes.array,
  onCreatePersona: PropTypes.func,
  onSelectPersona: PropTypes.func,
  onDeselectPersona: PropTypes.func,
  selectedPersona: PropTypes.object,
  isGeneratingPersona: PropTypes.bool,
  isViewer: PropTypes.bool,
};

ProductForm.defaultProps = {
  productDescription: '',
  setProductDescription: () => {},
  title: '',
  setTitle: () => {},
  audience: '',
  setAudience: () => {},
  businessGoal: '',
  setBusinessGoal: () => {},
  mode: 'form',
  reviewData: {},
  selectedCategory: REVIEW_CATEGORIES[0].key,
  selectedProblemChips: [],
  setSelectedProblemChips: () => {},
  aiFieldStates: null,
  isLoadingAiSuggestions: false,
  hasAiSuggestions: false,
  onRestoreAiSuggestions: () => {},
  onFieldEdit: null,
  onRestoreFieldAi: () => {},
  analyticsUploadState: null,
  analyticsFiles: null,
  onAnalyticsUpload: () => {},
  onAnalyticsRemove: () => {},
  disabled: false,
  observedAnalytics: '',
  setObservedAnalytics: () => {},
  personas: [],
  onCreatePersona: () => {},
  onSelectPersona: () => {},
  onDeselectPersona: () => {},
  selectedPersona: null,
  isGeneratingPersona: false,
  isViewer: false,
};

export default ProductForm;
